import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, ArrowRight, Bot, Building2, CheckCircle2, ClipboardList, Clock3, CreditCard, Gauge,
  KeyRound, Layers3, LifeBuoy, Link2, ListFilter, PackageCheck, Plus, RefreshCw, ScrollText, Search,
  ShieldAlert, ShieldCheck, UserPlus, Users,
} from "lucide-react";
import { tenantOnboardingStageLabels } from "@control-plane/contracts";
import type {
  SessionView, Tenant, TenantApplication, TenantAutomationOverview, TenantPlanOption, TenantPrecheckResult,
  TenantQueueCounts, TenantStatus, TenantUsageCenterView,
} from "@control-plane/contracts";
import { api } from "../lib/api";
import { useRemote } from "../lib/useRemote";
import { businessLabel, statusText } from "../lib/displayNames";
import { PageHeader } from "../components/PageHeader";
import { SectionCard } from "../components/SectionCard";
import { DataTable, type DataTableColumn } from "../components/DataTable";
import { StatusBadge } from "../components/StatusBadge";
import { RemotePageState } from "../components/RemotePageState";
import { Dialog } from "../components/Overlay";
import { Wizard, type WizardStep } from "../components/Wizard";
import { V3Select } from "../components/V3Select";
import { InlineFeedback } from "../components/Feedback";
import { ApprovalGateModal } from "../components/ApprovalGate";
import { AiInlineHelp } from "../components/AssistantInlineHelp";
import { V3LedgerQueueBand } from "../components/V3LedgerQueueBand";

type OwnerOption = { id: string; displayName: string };
type TenantsResponse = { items: Tenant[]; queue: TenantQueueCounts };
type WorkspaceData = TenantsResponse & { automation: TenantAutomationOverview; owners: OwnerOption[]; planOptions: TenantPlanOption[] };

const planLabels: Record<string, string> = { standard: "基础版", growth: "成长版", enterprise: "企业版" };
const regionLabels: Record<string, string> = { "cn-east": "华东", "cn-south": "华南", "cn-north": "华北" };
const featureStateLabels: Record<TenantUsageCenterView["features"][number]["items"][number]["state"], string> = { included: "已包含", temporary: "临时开放", unavailable: "不可用" };

/** 状态队列（CP-TENANT-002）：数量来自服务端全量口径，不做前端假统计。 */
type TenantQueueKey = "all" | "pending" | "provisioning" | "active" | "restricted" | "suspended" | "closed" | "attention";
const queueLabels: Record<TenantQueueKey, string> = {
  all: "全部租户", pending: "待处理", provisioning: "开通中", active: "可使用",
  restricted: "受限", suspended: "已暂停", closed: "已关闭", attention: "异常",
};
const queueIcons: Record<TenantQueueKey, typeof Building2> = {
  all: Building2, pending: UserPlus, provisioning: Clock3, active: CheckCircle2,
  restricted: Gauge, suspended: ShieldAlert, closed: Layers3, attention: AlertTriangle,
};
function queueCount(counts: TenantQueueCounts, key: TenantQueueKey): number {
  if (key === "all") return counts.total;
  if (key === "pending") return counts.pendingApplications;
  return counts[key];
}

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phonePattern = /^1\d{10}$/;

function auditActorLabel(actor: string): string {
  if (actor.startsWith("system:")) return "平台自动任务";
  if (actor.startsWith("service:erp-tenant-activation")) return "租户系统自动接入";
  if (actor.startsWith("service-account:")) return "系统服务账号";
  if (actor.startsWith("service-callback:")) return "外部服务回传";
  if (actor.startsWith("payment-webhook:")) return "支付平台回传";
  if (actor.startsWith("applicant:")) return "企业注册联系人";
  return actor;
}

export function TenantsPage({ session }: { session: SessionView }) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [planKey, setPlanKey] = useState("");
  const [owner, setOwner] = useState("");
  const [expiringFrom, setExpiringFrom] = useState("");
  const [expiringTo, setExpiringTo] = useState("");
  const [risk, setRisk] = useState("");
  const [queue, setQueue] = useState<TenantQueueKey>("all");
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const canCreate = session.actions.includes("tenant.create");
  const canManageApplications = session.actions.includes("tenant.applications.manage");

  const loader = useCallback(async (): Promise<WorkspaceData> => {
    const params = new URLSearchParams();
    if (query.trim()) params.set("q", query.trim());
    if (status) params.set("status", status);
    if (planKey) params.set("plan", planKey);
    if (owner) params.set("owner", owner);
    if (expiringFrom) params.set("expiringFrom", expiringFrom);
    if (expiringTo) params.set("expiringTo", expiringTo);
    if (risk) params.set("risk", risk);
    if (queue !== "all") params.set("queue", queue);
    const suffix = params.toString() ? `?${params.toString()}` : "";
    const [tenants, automation, profileOptions, planOptions] = await Promise.all([
      api<TenantsResponse>(`/api/tenants${suffix}`),
      api<TenantAutomationOverview>("/api/tenant-automation"),
      api<{ owners: OwnerOption[] }>("/api/tenant-profile-options"),
      api<{ items: TenantPlanOption[] }>("/api/tenant-plan-options"),
    ]);
    return { ...tenants, automation, owners: profileOptions.owners, planOptions: planOptions.items };
  }, [query, status, planKey, owner, expiringFrom, expiringTo, risk, queue]);
  const remote = useRemote(loader);

  return <div className="page tenant-control-page">
    <PageHeader
      icon={Building2}
      title="租户与申请"
      meta={remote.state.status === "ready" || remote.state.status === "empty"
        ? <span>共 <strong className="app-number">{remote.state.data.queue.total}</strong> 家企业 · 待处理申请 <strong className="app-number">{remote.state.data.queue.pendingApplications}</strong> 条</span>
        : <span>平台级范围 · 只读总览</span>}
      actions={<>
        <button className="button secondary" onClick={() => void remote.reload()}><RefreshCw size={16} />刷新</button>
        {canCreate && <button className="button primary" onClick={() => setShowCreate(true)}><Plus size={17} />新建租户</button>}
      </>} />
    <RemotePageState state={remote.state} onRetry={remote.reload}>{(data) =>
      <TenantControlWorkspace
        data={data}
        query={query} onQuery={setQuery}
        status={status} onStatus={setStatus}
        planKey={planKey} onPlanKey={setPlanKey}
        owner={owner} onOwner={setOwner}
        expiringFrom={expiringFrom} onExpiringFrom={setExpiringFrom}
        expiringTo={expiringTo} onExpiringTo={setExpiringTo}
        risk={risk} onRisk={setRisk}
        queue={queue} onQueue={setQueue}
        onOpen={setSelected} />
    }</RemotePageState>
    <TenantApplications owners={remote.state.status === "ready" || remote.state.status === "empty" ? remote.state.data.owners : []} planOptions={remote.state.status === "ready" || remote.state.status === "empty" ? remote.state.data.planOptions : []} canManage={canManageApplications} onChanged={remote.reload} />
    {showCreate && <CreateTenant owners={remote.state.status === "ready" || remote.state.status === "empty" ? remote.state.data.owners : []} planOptions={remote.state.status === "ready" || remote.state.status === "empty" ? remote.state.data.planOptions : []} onClose={() => setShowCreate(false)} onDone={() => { setShowCreate(false); void remote.reload(); }} />}
    {selected && <TenantDetail tenantId={selected} session={session} onClose={() => setSelected(null)} onChanged={remote.reload} />}
  </div>;
}

function TenantControlWorkspace({ data, query, onQuery, status, onStatus, planKey, onPlanKey, owner, onOwner, expiringFrom, onExpiringFrom, expiringTo, onExpiringTo, risk, onRisk, queue, onQueue, onOpen }: {
  data: WorkspaceData;
  query: string; onQuery: (value: string) => void;
  status: string; onStatus: (value: string) => void;
  planKey: string; onPlanKey: (value: string) => void;
  owner: string; onOwner: (value: string) => void;
  expiringFrom: string; onExpiringFrom: (value: string) => void;
  expiringTo: string; onExpiringTo: (value: string) => void;
  risk: string; onRisk: (value: string) => void;
  queue: TenantQueueKey; onQueue: (value: TenantQueueKey) => void;
  onOpen: (tenantId: string) => void;
}) {
  const stats = useMemo(() => summarizeTenants(data.items), [data.items]);
  const queueItems = useMemo(() => (Object.keys(queueLabels) as TenantQueueKey[]).map((key) => ({
    key, label: queueLabels[key], count: queueCount(data.queue, key), icon: queueIcons[key],
  })), [data.queue]);
  const ownerOptions = useMemo(() => [
    { value: "", label: "全部负责人" },
    ...data.owners.map((item) => ({ value: item.id, label: item.displayName })),
  ], [data.owners]);
  const columns = useMemo<Array<DataTableColumn<Tenant>>>(() => [
    { key: "tenant", header: "租户", render: (tenant) => <div className="entity tenant-entity"><span><Building2 size={16} /></span><div><strong>{tenant.name}</strong></div></div> },
    { key: "stage", header: "当前阶段", render: (tenant) => <><StatusBadge value={tenant.status} /><small className="block">{stageHint(tenant)}</small></> },
    { key: "plan", header: "客户版本", render: (tenant) => <><strong>{businessLabel(planLabels, tenant.planKey, "版本待确认")}</strong><small className="block">{businessLabel(regionLabels, tenant.region, "区域待确认")}</small></> },
    { key: "owner", header: "负责人", render: (tenant) => tenant.ownerName ? <span>{tenant.ownerName}</span> : <small className="muted">未指派</small> },
    { key: "readiness", header: "安全检查", width: "170px", render: (tenant) => tenant.status === "active" && tenant.isolationReadiness < 100
      ? <div className="readiness-cell active-pending"><ShieldCheck size={15} /><strong>待补检</strong><small>{tenant.isolationReadiness}% 已完成</small></div>
      : <div className={`readiness-cell ${readinessTone(tenant.isolationReadiness)}`}><div className="progress"><i style={{ width: `${tenant.isolationReadiness}%` }} /></div><strong>{tenant.isolationReadiness}%</strong></div> },
    { key: "focus", header: "处理状态", render: (tenant) => <span className="governance-focus"><ListFilter size={14} />{governanceFocus(tenant)}</span> },
    { key: "updated", header: "更新时间", render: (tenant) => <><time>{formatDate(tenant.updatedAt || tenant.createdAt)}</time><small className="block">创建 {formatDate(tenant.createdAt)}</small></> },
  ], []);
  return <>
    <SectionCard icon={Bot} title="自动接入引擎" meta={<span>合格注册实时处理 · 尾单每 {data.automation.retryIntervalSeconds} 秒自动重试</span>}>
      <div className="tenant-command-metrics">
        <TenantMetric label="自动处理" value={data.automation.automaticApproved} detail={`${data.automation.provisioned} 个已完成接入`} icon={CheckCircle2} />
        <TenantMetric label="等待重试" value={data.automation.pendingRetry} detail={`单批最多 ${data.automation.maxBatchSize} 个`} icon={RefreshCw} />
        <TenantMetric label="人工例外" value={data.automation.manualReview} detail="只处理异常资料" icon={AlertTriangle} {...(data.automation.manualReview ? { tone: "attention" as const } : {})} />
        <TenantMetric label="开通中" value={data.queue.provisioning} detail="系统自动完成安全检查" icon={ShieldCheck} />
      </div>
      <p className="home-stat-note">只有记录缺失或凭据过期才转人工处理；自动接入全程写入审计链，无需人工值守。</p>
    </SectionCard>

    <SectionCard icon={Building2} title="租户生命周期台账" variant="ledger" className="tenant-ledger-card"
      filters={<div className="tenant-ledger-command">
        <div className="search tenant-search"><Search size={17} /><input value={query} onChange={(event) => onQuery(event.target.value)} placeholder="搜索企业名称或联系人" aria-label="搜索租户" /></div>
        <div className="tenant-filter-bar" aria-label="台账筛选条件">
          <V3Select value={status} onChange={onStatus} ariaLabel="按状态筛选" options={[
            { value: "", label: "全部状态" },
            { value: "provisioning", label: "配置中" },
            { value: "active", label: "运行中" },
            { value: "suspended", label: "已暂停" },
            { value: "archived", label: "已归档" },
            { value: "purged", label: "已永久注销" },
          ]} />
          <V3Select value={planKey} onChange={onPlanKey} ariaLabel="按套餐筛选" options={[
            { value: "", label: "全部套餐" },
            { value: "standard", label: "基础版" },
            { value: "growth", label: "成长版" },
            { value: "enterprise", label: "企业版" },
          ]} />
          <V3Select value={owner} onChange={onOwner} ariaLabel="按负责人筛选" options={ownerOptions} />
          <V3Select value={risk} onChange={onRisk} ariaLabel="按风险筛选" options={[
            { value: "", label: "全部风险" },
            { value: "attention", label: "需关注" },
            { value: "expiring", label: "即将到期" },
          ]} />
          <label className="tenant-filter-date"><span>到期范围</span>
            <input type="date" value={expiringFrom} onChange={(event) => onExpiringFrom(event.target.value)} aria-label="到期起始日期" />
            <span aria-hidden>至</span>
            <input type="date" value={expiringTo} onChange={(event) => onExpiringTo(event.target.value)} aria-label="到期截止日期" />
          </label>
        </div>
      </div>}>
      <V3LedgerQueueBand ariaLabel="租户状态队列" items={queueItems} value={queue} onChange={onQueue} />
      <DataTable columns={columns} rows={data.items} rowKey={(tenant) => tenant.id} ariaLabel="租户生命周期台账"
        rowActions={(tenant) => <button className="button ghost" onClick={() => onOpen(tenant.id)}>使用中心 <ArrowRight size={15} /></button>}
        empty={<span>当前筛选条件下没有租户。可以调整上方状态队列或筛选条件，或通过自助注册、新建租户接入第一家企业。</span>} />
      {stats.total === 0 && data.queue.total > 0 && <p className="home-stat-note">筛选结果为空：当前条件排除了全部 {data.queue.total} 个租户，可重置筛选后查看。</p>}
    </SectionCard>
  </>;
}

function TenantMetric({ icon: Icon, label, value, detail, tone }: { icon: typeof Building2; label: string; value: number | string; detail: string; tone?: "attention" }) {
  return <article className={tone ? `tenant-metric-${tone}` : undefined}><span><Icon size={16} />{label}</span><strong>{value}</strong><small>{detail}</small></article>;
}

function summarizeTenants(items: Tenant[]) {
  const total = items.length;
  const active = items.filter((tenant) => tenant.status === "active").length;
  const provisioning = items.filter((tenant) => tenant.status === "provisioning").length;
  const suspended = items.filter((tenant) => tenant.status === "suspended").length;
  const closed = items.filter((tenant) => tenant.status === "archived" || tenant.status === "purged").length;
  return { total, active, provisioning, suspended, closed };
}

function readinessTone(value: number) {
  if (value >= 100) return "ready";
  if (value >= 60) return "watch";
  return "blocked";
}

function stageHint(tenant: Tenant) {
  if (tenant.status === "active" && tenant.isolationReadiness < 100) return "内部测试已激活";
  if (tenant.status === "active") return "可被租户系统使用";
  if (tenant.status === "provisioning") return "基础版可用，系统配置中";
  if (tenant.status === "suspended") return "暂不开放客户使用";
  if (tenant.status === "purged") return "控制面使用资格已永久停止";
  return "已关闭，仅保留审计查询";
}

function governanceFocus(tenant: Tenant) {
  if (tenant.status === "active" && tenant.isolationReadiness < 100) return "可测试，安全检查仍需补齐";
  if (tenant.status === "active") return "正常使用，持续自动巡检";
  if (tenant.status === "provisioning") return "系统自动完成安全检查";
  if (tenant.status === "suspended") return tenant.suspensionReason ? `暂停原因：${tenant.suspensionReason}` : "先确认暂停原因与恢复条件";
  if (tenant.status === "purged") return "永久注销完成，不可恢复";
  return "已关闭，如需重开需重新评估";
}

function formatDate(value: string) {
  return new Date(value).toLocaleDateString("zh-CN");
}

function formatDateTime(value: string | null) {
  return value ? new Date(value).toLocaleString("zh-CN") : "—";
}

/** 申请队列（CP-TENANT-008）：自动通过、人工复核、合并重复、拒绝、重试、转交。 */
function TenantApplications({ owners, planOptions, canManage, onChanged }: { owners: OwnerOption[]; planOptions: TenantPlanOption[]; canManage: boolean; onChanged: () => void }) {
  const remote = useRemote(useCallback(() => api<{ items: TenantApplication[] }>("/api/tenant-applications"), []));
  const [action, setAction] = useState<{ kind: "approve" | "reject" | "transfer" | "merge"; application: TenantApplication } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowError, setRowError] = useState("");
  async function retry(application: TenantApplication) {
    setBusyId(application.id); setRowError("");
    try {
      await api(`/api/tenant-applications/${application.id}/retry`, { method: "POST", body: JSON.stringify({}) });
      await remote.reload();
    } catch (failure) {
      setRowError(failure instanceof Error ? failure.message : "重试失败，请稍后再试");
    } finally { setBusyId(null); }
  }
  const items = remote.state.status === "ready" || remote.state.status === "empty" ? remote.state.data.items : [];
  return <SectionCard icon={UserPlus} title="自助注册流水" variant="ledger" meta={<span>合格注册由系统实时处理，异常才进入人工队列</span>}>
    <RemotePageState state={remote.state} onRetry={remote.reload} emptyHint="还没有租户自助注册记录，首个申请会由系统自动处理。">{(data) => <>
      <div className="application-summary">
        <ApplicationMetric label="等待自动重试" value={data.items.filter((item) => item.status === "pending").length} />
        <ApplicationMetric label="已自动批准" value={data.items.filter((item) => item.status === "approved").length} />
        <ApplicationMetric label="已完成接入" value={data.items.filter((item) => item.status === "provisioned").length} />
        <ApplicationMetric label="已拒绝" value={data.items.filter((item) => item.status === "rejected").length} />
        <ApplicationMetric label="已合并重复" value={data.items.filter((item) => item.status === "merged").length} />
      </div>
      <DataTable columns={[
        { key: "company", header: "企业 / 联系人", render: (item) => <><strong>{item.companyName}</strong><small className="block">{item.contactName}</small></> },
        { key: "status", header: "状态", render: (item) => <><StatusBadge value={item.status} />{item.assignedToName && <small className="block">处理人：{item.assignedToName}</small>}</> },
        { key: "route", header: "处理方式", render: (item) => <span className="automation-route">{applicationRoute(item)}</span> },
        { key: "progress", header: "自动接入情况", render: (item) => <>
          <small className="block">{`开通阶段：${tenantOnboardingStageLabels[item.onboardingStage] ?? "资料已确认"}`}</small>
          {item.onboardingFailedStage && <small className="block">{`最近卡点：${tenantOnboardingStageLabels[item.onboardingFailedStage] ?? ""}${item.lastFailureReason ? ` · ${item.lastFailureReason}` : ""}`}</small>}
          {item.status === "pending" && item.retryCount > 0
            ? <small className="block">{`已自动重试 ${item.retryCount} 次${item.lastFailureReason ? ` · 最近失败：${item.lastFailureReason}` : ""}`}</small>
            : item.status === "pending" && <small className="muted">首次排队中</small>}
        </> },
        { key: "contact", header: "联系方式", render: (item) => <><strong>{item.email}</strong><small className="block">{item.phoneMasked}</small></> },
        { key: "created", header: "注册时间", render: (item) => new Date(item.createdAt).toLocaleString("zh-CN") },
      ]} rows={data.items} rowKey={(item) => item.id} ariaLabel="自助注册流水"
        {...(canManage ? { rowActions: (item: TenantApplication) => item.status === "pending" ? <div className="application-actions">
          <button className="button ghost" onClick={() => setAction({ kind: "approve", application: item })}>通过并接入</button>
          <button className="button ghost" onClick={() => setAction({ kind: "reject", application: item })}>拒绝</button>
          <button className="button ghost" disabled={busyId === item.id} onClick={() => void retry(item)}>{busyId === item.id ? "重试中…" : "重试"}</button>
          <button className="button ghost" onClick={() => setAction({ kind: "transfer", application: item })}>转交</button>
          <button className="button ghost" onClick={() => setAction({ kind: "merge", application: item })}>合并重复</button>
        </div> : <small className="muted">已处理</small> } : {})}
        empty={<span>还没有租户自助注册记录，首个申请会由系统自动处理。</span>} />
      {rowError && <div className="application-row-error"><InlineFeedback tone="danger">{rowError}</InlineFeedback></div>}
    </>}</RemotePageState>
    {action && <ApplicationActionDialog
      kind={action.kind}
      application={action.application}
      owners={owners}
      planOptions={planOptions}
      applications={items.filter((item) => item.status === "pending" && item.id !== action.application.id)}
      onClose={() => setAction(null)}
      onDone={() => { setAction(null); void remote.reload(); onChanged(); }} />}
  </SectionCard>;
}

function applicationRoute(application: TenantApplication) {
  if (application.reviewNote?.startsWith("自动接入策略")) return "系统自动处理";
  if (application.status === "pending") return "等待自动重试";
  if (application.status === "rejected") return "人工例外处置";
  if (application.status === "merged") return "合并到重复申请";
  return "历史接入流程";
}

function ApplicationMetric({ label, value }: { label: string; value: number }) {
  return <span><strong className="app-number">{value}</strong>{label}</span>;
}

function ApplicationActionDialog({ kind, application, owners, planOptions, applications, onClose, onDone }: {
  kind: "approve" | "reject" | "transfer" | "merge";
  application: TenantApplication;
  owners: OwnerOption[];
  planOptions: TenantPlanOption[];
  applications: TenantApplication[];
  onClose: () => void;
  onDone: () => void;
}) {
  const copy = {
    approve: { title: "通过并接入", description: "通过后系统立即按所选套餐和数据区域完成自动接入，并写入审计记录。" },
    reject: { title: "拒绝申请", description: "拒绝后该企业需重新提交注册申请，本条记录保留审计。" },
    transfer: { title: "转交处理人", description: "转交给指定的平台管理员继续跟进本条申请。" },
    merge: { title: "合并重复申请", description: "将本条申请合并到另一条重复申请下，由后者统一处理；本条标记为已合并。" },
  }[kind];
  const [note, setNote] = useState("");
  const [planVersionId, setPlanVersionId] = useState("");
  const [region, setRegion] = useState("cn-east");
  const [toAdminId, setToAdminId] = useState("");
  const [mergeIntoApplicationId, setMergeIntoApplicationId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const noteReady = note.trim().length >= 8;
  async function submit() {
    setBusy(true); setError("");
    try {
      if (kind === "approve") {
        await api(`/api/tenant-applications/${application.id}/review`, { method: "POST", body: JSON.stringify({ decision: "approve", note: note.trim(), planVersionId, region }) });
      } else if (kind === "reject") {
        await api(`/api/tenant-applications/${application.id}/review`, { method: "POST", body: JSON.stringify({ decision: "reject", note: note.trim() }) });
      } else if (kind === "transfer") {
        await api(`/api/tenant-applications/${application.id}/transfer`, { method: "POST", body: JSON.stringify({ toAdminId, note: note.trim() }) });
      } else {
        await api(`/api/tenant-applications/${application.id}/merge`, { method: "POST", body: JSON.stringify({ mergeIntoApplicationId, note: note.trim() }) });
      }
      onDone();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "操作失败，请稍后再试");
    } finally { setBusy(false); }
  }
  const blocked = (kind === "transfer" && !toAdminId) || (kind === "merge" && !mergeIntoApplicationId) || (kind === "approve" && !planVersionId);
  return <Dialog title={copy.title} description={`${copy.description} 影响对象：${application.companyName}`} onClose={onClose}>
    <div className="form-grid">
      {kind === "approve" && <>
        <div className="field-block"><span className="field-label-row">接入套餐</span>
          {planOptions.length > 0
            ? <V3Select value={planVersionId} onChange={setPlanVersionId} ariaLabel="接入套餐" options={planOptions.map((plan) => ({
                value: plan.planVersionId,
                label: `${plan.planName}（已发布第 ${plan.version} 版）`,
                hint: `可用功能 ${plan.capabilitiesCount} 项 · 资源额度 ${plan.meterLimitsCount} 项`,
              }))} />
            : <div className="form-error"><InlineFeedback tone="warning">当前没有在售套餐版本，无法通过接入；请先在套餐目录中发布可售套餐。</InlineFeedback></div>}
        </div>
        <div className="field-block"><span className="field-label-row">数据区域</span>
          <V3Select value={region} onChange={setRegion} ariaLabel="接入数据区域" options={[
            { value: "cn-east", label: "华东", hint: "默认区域" },
            { value: "cn-south", label: "华南" },
            { value: "cn-north", label: "华北" },
          ]} />
        </div>
      </>}
      {kind === "transfer" && <div className="full field-block"><span className="field-label-row">转交给</span>
        <V3Select value={toAdminId} onChange={setToAdminId} ariaLabel="转交处理人" options={owners.map((item) => ({ value: item.id, label: item.displayName }))} />
      </div>}
      {kind === "merge" && <div className="full field-block"><span className="field-label-row">合并到哪条申请</span>
        <V3Select value={mergeIntoApplicationId} onChange={setMergeIntoApplicationId} ariaLabel="合并目标申请" options={applications.map((item) => ({ value: item.id, label: `${item.companyName}（${item.email}）` }))} />
      </div>}
      <label className="full"><span className="field-label-row">处理说明（至少 8 个字，会写入审计）</span>
        <textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} placeholder="说明确认的事实和处置原因，例如联系企业核实了重复注册情况" />
      </label>
      {error && <div className="full"><InlineFeedback tone="danger">{error}</InlineFeedback></div>}
      <div className="full dialog-footer">
        <button type="button" className="button secondary" onClick={onClose} disabled={busy}>取消</button>
        <button type="button" className="button primary" disabled={busy || !noteReady || blocked} onClick={() => void submit()}>{busy ? "正在提交…" : copy.title}</button>
      </div>
    </div>
  </Dialog>;
}

/** 新建租户（CP-TENANT-003/004）：企业信息→管理员→初始套餐→数据区域→确认；技术编号由服务端生成，创建前真实预检（失败关闭）。 */
function CreateTenant({ owners, planOptions, onClose, onDone }: { owners: OwnerOption[]; planOptions: TenantPlanOption[]; onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState(() => ({
    name: "", ownerAdminId: "", planVersionId: "", region: "cn-east",
    contactName: "", contactEmail: "", contactPhone: "",
  }));
  const [precheckState, setPrecheckState] = useState<PrecheckStatus>("idle");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit() {
    setBusy(true); setError("");
    try {
      await api("/api/tenants", { method: "POST", body: JSON.stringify({
        name: form.name.trim(), planVersionId: form.planVersionId, region: form.region,
        ...(form.ownerAdminId ? { ownerAdminId: form.ownerAdminId } : {}),
        ...(form.contactName.trim() ? { contactName: form.contactName.trim() } : {}),
        ...(form.contactEmail.trim() ? { contactEmail: form.contactEmail.trim() } : {}),
        ...(form.contactPhone.trim() ? { contactPhone: form.contactPhone.trim() } : {}),
      })});
      onDone();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "创建失败，请稍后再试");
    } finally { setBusy(false); }
  }
  const ownerLabel = owners.find((item) => item.id === form.ownerAdminId)?.displayName ?? "暂不指派";
  const selectedPlan = planOptions.find((plan) => plan.planVersionId === form.planVersionId);
  const steps: WizardStep[] = [
    {
      key: "identity", title: "企业信息", summary: "填写企业名称；客户看到的一直是企业名称，技术编号由系统自动生成。",
      content: <div className="form-grid">
        <div className="full field-block"><span className="field-label-row">企业名称<AiInlineHelp label="问 AI" prompt="新建租户里的「企业名称」应该怎么填？请用小白能懂的话说明它会显示在哪里、建议格式、示例、填错后有什么影响。" /></span><input aria-label="企业名称" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus /></div>
      </div>,
      validate: () => form.name.trim().length < 2 ? "请填写至少 2 个字的企业名称。" : null,
    },
    {
      key: "admin", title: "管理员", summary: "登记企业联系人和平台负责人；创建前系统会真实检查联系方式的重复情况。",
      content: <div className="form-grid">
        <div className="full field-block"><span className="field-label-row">平台负责人</span>
          <V3Select value={form.ownerAdminId} onChange={(value) => setForm({ ...form, ownerAdminId: value })} ariaLabel="平台负责人"
            options={[{ value: "", label: "暂不指派", hint: "创建后可在使用中心维护" }, ...owners.map((item) => ({ value: item.id, label: item.displayName }))]} />
        </div>
        <label><span className="field-label-row">企业联系人</span><input value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })} placeholder="例如：王经理" /></label>
        <label><span className="field-label-row">联系邮箱</span><input value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} inputMode="email" placeholder="用于重复检查和后续通知" /></label>
        <label><span className="field-label-row">联系手机（可选）</span><input value={form.contactPhone} onChange={(e) => setForm({ ...form, contactPhone: e.target.value })} inputMode="numeric" placeholder="11 位手机号" /></label>
      </div>,
      validate: () => {
        if (form.contactName.trim() && form.contactName.trim().length < 2) return "企业联系人至少 2 个字，或留空暂不登记。";
        if (form.contactEmail.trim() && !emailPattern.test(form.contactEmail.trim())) return "联系邮箱格式不正确，请检查后重填。";
        if (form.contactPhone.trim() && !phonePattern.test(form.contactPhone.trim())) return "联系手机需为 11 位中国大陆手机号，或留空。";
        return null;
      },
    },
    {
      key: "plan", title: "初始套餐", summary: "选择创建时立即生效的在售套餐版本，系统会同时建立真实订阅与授权；后续升级在客户与商业的订阅管理中操作。",
      content: <div className="form-grid">
        <div className="full field-block"><span className="field-label-row">初始套餐<AiInlineHelp label="问 AI" prompt="新建租户时「初始套餐」怎么选？请用小白能懂的话解释各套餐的区别，以及选错后是否可以更换。" /></span>
          {planOptions.length > 0
            ? <V3Select value={form.planVersionId} onChange={(value) => setForm({ ...form, planVersionId: value })} ariaLabel="初始套餐"
                options={planOptions.map((plan) => ({
                  value: plan.planVersionId,
                  label: `${plan.planName}（已发布第 ${plan.version} 版）`,
                  hint: `可用功能 ${plan.capabilitiesCount} 项 · 资源额度 ${plan.meterLimitsCount} 项`,
                }))} />
            : <div className="form-error"><InlineFeedback tone="warning">当前没有在售套餐版本，暂不能新建租户；请先在套餐目录中发布可售套餐。</InlineFeedback></div>}
        </div>
      </div>,
      validate: () => !form.planVersionId ? "请选择一个在售的初始套餐。" : null,
    },
    {
      key: "region", title: "数据区域", summary: "数据区域决定这家企业的数据存放位置，创建后不可随意变更。",
      content: <div className="form-grid">
        <div className="full field-block"><span className="field-label-row">数据区域<AiInlineHelp label="问 AI" prompt="新建租户里的「数据区域」怎么选？请解释华东、华南、华北对客户使用、数据和后续运维有什么影响。" /></span>
          <V3Select value={form.region} onChange={(value) => setForm({ ...form, region: value })} ariaLabel="数据区域"
            options={[
              { value: "cn-east", label: "华东", hint: "默认区域，适合大多数企业" },
              { value: "cn-south", label: "华南", hint: "华南及东南亚客户访问更快" },
              { value: "cn-north", label: "华北", hint: "华北客户访问更快" },
            ]} />
        </div>
      </div>,
    },
    {
      key: "confirm", title: "确认创建", summary: "系统已自动进行企业与联系方式重复检查；只有检查通过后才能提交。",
      content: <div className="form-grid">
        <div className="full confirm-summary">
          <div><span>企业名称</span><strong>{form.name}</strong></div>
          <div><span>平台负责人</span><strong>{ownerLabel}</strong></div>
          <div><span>企业联系人</span><strong>{form.contactName || "暂不登记"}</strong>{form.contactEmail && <small>{form.contactEmail}{form.contactPhone ? ` · ${form.contactPhone}` : ""}</small>}</div>
          <div><span>初始套餐</span><strong>{selectedPlan ? `${selectedPlan.planName}（已发布第 ${selectedPlan.version} 版）` : "未选择"}</strong></div>
          <div><span>数据区域</span><strong>{businessLabel(regionLabels, form.region, "区域待确认")}</strong></div>
        </div>
        <div className="full">
          <PrecheckPanel name={form.name} contactEmail={form.contactEmail} contactPhone={form.contactPhone} onStateChange={setPrecheckState} />
          {precheckState !== "passed" && <p className="precheck-note">提交按钮会在创建前检查通过后自动开放。</p>}
        </div>
      </div>,
    },
  ];
  return <Wizard title="新建租户" description="创建成功后系统按所选套餐建立真实订阅并发放使用资格；全部信息可在使用中心维护。" steps={steps}
    submitLabel="创建租户" onSubmit={() => void submit()} onClose={onClose} submitting={busy}
    submitDisabled={precheckState !== "passed"}
    submitError={error || (precheckState === "blocked" ? "存在阻断性重复，请先处理上方提示后再提交。" : precheckState === "failed" ? "创建前检查暂时不可用，为避免重复创建已暂停提交，请稍后重试。" : null)} />;
}

/** 创建前检查状态：只有 passed 才允许提交（失败关闭）。 */
type PrecheckStatus = "idle" | "checking" | "passed" | "blocked" | "failed";

/** 创建前真实预检（CP-TENANT-004）：企业/管理员/域名重复与激活安全条件；请求失败或结果过期一律阻断提交。 */
function PrecheckPanel({ name, contactEmail, contactPhone, onStateChange }: {
  name: string; contactEmail: string; contactPhone: string; onStateChange: (status: PrecheckStatus) => void;
}) {
  const [result, setResult] = useState<TenantPrecheckResult | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const trimmedName = name.trim();
  const email = contactEmail.trim();
  const phone = contactPhone.trim();
  useEffect(() => {
    // 输入一变，旧结果立即视为过期（过期期间不可提交）。
    onStateChange("idle");
    if (trimmedName.length < 2) { setResult(null); setError(""); return; }
    let stale = false;
    const timer = setTimeout(async () => {
      if (stale) return;
      setBusy(true); setError("");
      onStateChange("checking");
      try {
        const data = await api<TenantPrecheckResult>("/api/tenants/precheck", {
          method: "POST",
          body: JSON.stringify({ name: trimmedName, ...(email ? { contactEmail: email } : {}), ...(phone ? { contactPhone: phone } : {}) }),
        });
        if (stale) return; // 丢弃过期结果：它对应的不是当前表单值。
        setResult(data);
        onStateChange(data.duplicateCompany.blocked || data.duplicateContact.blocked ? "blocked" : "passed");
      } catch (failure) {
        if (stale) return;
        setResult(null); // 失败关闭：无有效结果不可提交。
        setError(failure instanceof Error ? failure.message : "重复检查暂时不可用，请稍后重试");
        onStateChange("failed");
      } finally {
        if (!stale) setBusy(false);
      }
    }, 500);
    return () => { stale = true; clearTimeout(timer); };
  }, [trimmedName, email, phone]);
  return <div className="precheck-panel" aria-live="polite">
    <div className="precheck-heading"><ShieldCheck size={16} /><strong>创建前检查</strong><span>{busy ? "检查中…" : result ? "已完成" : "待填写"}</span></div>
    {error && <InlineFeedback tone="danger">{error}</InlineFeedback>}
    {result && <>
      {result.duplicateCompany.blocked
        ? <InlineFeedback tone="danger">{`已存在同名企业「${result.duplicateCompany.matchName ?? ""}」，不能重复创建。`}</InlineFeedback>
        : <p className="precheck-ok"><CheckCircle2 size={14} />企业名称未被占用</p>}
      {result.duplicateContact.blocked
        ? <InlineFeedback tone="danger">{result.duplicateContact.field === "email" ? "该邮箱已登记在其他企业名下，不能重复使用。" : "该手机号已登记在其他企业名下，不能重复使用。"}</InlineFeedback>
        : (email || phone) && <p className="precheck-ok"><CheckCircle2 size={14} />联系方式未被占用</p>}
      {result.duplicateDomain.warning && <InlineFeedback tone="warning">{`联系邮箱域名与「${result.duplicateDomain.domain ?? ""}」相关的已有企业一致，请确认是否属于同一企业。`}</InlineFeedback>}
      <p className="precheck-note">{result.activationSafety.explanation}</p>
    </>}
  </div>;
}

/** 生命周期动作文案（CP-TENANT-006）：暂停 / 恢复 / 关闭 / 永久注销，危险动作展示影响预览与恢复条件。 */
const lifecycleCopy: Record<TenantStatus, { title: string; impact: string[]; recovery: string }> = {
  active: { title: "激活或恢复租户", impact: ["该租户的账号将可以进入工作台", "功能按当前套餐授权立即生效"], recovery: "激活与暂停都是可逆的生命周期状态，可再次发起审批调整；全部变更都会写入审计链。" },
  suspended: { title: "暂停客户使用", impact: ["该租户的客户将立即无法进入工作台", "租户数据与审计记录完整保留，不会被删除"], recovery: "确认问题处理完成后，可再次发起审批恢复使用；审计记录完整保留。" },
  archived: { title: "关闭租户", impact: ["该租户不再开放业务使用", "仅保留审计查询与合规记录", "关闭后不能直接恢复，重新开放需重新评估"], recovery: "如确需重新开放，需由平台负责人重新评估后走新建流程；审计记录长期保留。" },
  provisioning: { title: "变更租户生命周期", impact: ["该租户的生命周期状态将变更"], recovery: "生命周期状态可通过再次审批调整。" },
  purged: { title: "永久注销（不可恢复）", impact: ["永久停止该租户通过平台控制面获得使用资格", "通过签名生命周期事件停用 ERP 企业访问并吊销该企业全部会话", "已停用的控制面配置、ERP 业务数据和审计记录按合规要求保留", "本操作不读取、也不删除租户 ERP 里的订单、商品、库存、客户、薪资和财务数据", "ERP 业务数据如需删除，必须在租户系统中按独立授权与合规流程执行", "执行前需先吊销其全部服务账号与平台凭据，并配置 ERP 生命周期接收地址"], recovery: "永久注销控制面身份不可恢复。如仅需暂停使用，应选择「暂停」或「关闭租户」。" },
};

/** 租户 360 使用中心（CP-TENANT-360）：只读控制面元数据，九个分区。 */
function TenantDetail({ tenantId, session, onClose, onChanged }: { tenantId: string; session: SessionView; onClose: () => void; onChanged: () => void }) {
  const loader = useCallback(() => api<TenantUsageCenterView>(`/api/governance/tenants/${tenantId}/usage-center`), [tenantId]);
  const remote = useRemote(loader);
  const [gate, setGate] = useState<{ status: TenantStatus; tenantName: string } | null>(null);
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const canUpdateProfile = session.actions.includes("tenant.update");
  async function executeTransition(status: TenantStatus, approvalId: string, reason: string) {
    await api(`/api/tenants/${tenantId}/transition`, { method: "POST", body: JSON.stringify({ status, reason, approvalId }) });
  }

  return <>
  <Dialog wide className="tenant-governance-modal" title="租户使用中心" description="平台侧管理信息总览，不涉及客户业务数据。" onClose={onClose}>
    <RemotePageState state={remote.state} onRetry={remote.reload}>{(view) => {
      const { tenant, subscription, readiness, activation } = view;
      const ready = readiness.failed === 0 && readiness.percent >= 100;
      const restricted = subscription?.status === "past_due" || subscription?.status === "frozen";
      const purged = tenant.status === "purged";
      const archived = tenant.status === "archived";
      const suspended = tenant.status === "suspended";
      const active = tenant.status === "active";
      const canActivate = !active && !suspended && !archived && !purged;
      // P2 硬返工一.5：结论必须优先依据 activation.blocked——阻断时明确「安全验证尚未完成、暂不能进入工作台、恢复条件」，
      // 不得出现任何形式的假成功表述。
      const outcome = activation.blocked
        ? {
          tone: "attention", icon: ShieldAlert, eyebrow: "当前状态", title: "安全验证尚未完成，暂不能进入工作台",
          detail: "当前平台已有正式运行租户，本租户暂不能激活。恢复条件：完成跨租户安全验证后，由平台解除单租户安全保护；解除后系统会自动继续开通。",
        }
        : active && readiness.percent < 100
        ? { tone: "attention", icon: ShieldCheck, eyebrow: "当前状态", title: "内部测试已激活", detail: `账号已由平台负责人放行，可用于内部测试；安全检查当前完成 ${readiness.percent}%，仍需继续补齐。` }
        : active
        ? { tone: "ready", icon: CheckCircle2, eyebrow: "当前状态", title: "客户可以正常使用", detail: restricted ? "订阅状态异常，功能可能受限，请先核对下方订阅信息。" : "功能已按当前套餐开放，系统持续监测安全状态和运行异常。" }
        : suspended
          ? { tone: "paused", icon: ShieldAlert, eyebrow: "当前状态", title: "客户已被暂停使用", detail: tenant.suspensionReason ? `暂停原因：${tenant.suspensionReason}` : "这是人工暂停状态，确认问题处理完成后可以恢复。" }
          : archived
            ? { tone: "archived", icon: Layers3, eyebrow: "当前状态", title: "该租户已经关闭", detail: "当前仅保留审计查询能力，不再开放业务使用。" }
            : purged
              ? { tone: "archived", icon: ShieldAlert, eyebrow: "当前状态", title: "该租户已永久注销", detail: "控制面使用资格已永久停止，已停用的配置与审计记录按合规要求保留。" }
              : { tone: readiness.failed > 0 ? "attention" : "basic", icon: readiness.failed > 0 ? ShieldAlert : PackageCheck, eyebrow: "基础使用", title: "开通进行中，完成激活后可进入工作台", detail: readiness.failed > 0 ? "平台检测到安全异常，技术团队正在处理；处理完成并通过激活后才会开放工作台。" : "系统已按套餐策略发放使用资格，客户完成激活后即可进入工作台。" };
      const OutcomeIcon = outcome.icon;
      const risks = [
        ...(readiness.failed > 0 ? [`安全检查未通过 ${readiness.failed} 项`] : []),
        ...(subscription?.status === "past_due" ? ["订阅待支付"] : []),
        ...(subscription?.status === "frozen" ? ["订阅已冻结"] : []),
        ...(suspended && !activation.blocked ? ["客户使用已暂停"] : []),
      ];
      const unavailableServiceLabels = [
        ...(view.features.length === 0 ? ["功能目录"] : []),
        ...(view.usage.length === 0 ? ["用量额度"] : []),
        ...(view.connections.length === 0 ? ["平台连接"] : []),
        ...(view.serviceAccounts.length === 0 ? ["服务账号"] : []),
        ...(view.supportGrants.length === 0 ? ["支持授权"] : []),
      ];
      return <div className="tenant-360">
        <div className="tenant-opening-identity">
          <div className="tenant-opening-mark"><Building2 size={22} /></div>
          <div><span>企业名称</span><h3>{tenant.name}</h3></div>
          <div className="tenant-opening-meta"><StatusBadge value={tenant.status} /><span>{businessLabel(planLabels, tenant.planKey, "版本待确认")} · {businessLabel(regionLabels, tenant.region, "区域待确认")}</span></div>
        </div>

        <div className="tenant-360-view">
          <section className={`tenant-opening-outcome tenant-360-span ${outcome.tone}`}>
            <OutcomeIcon size={24} />
            <div><p>{outcome.eyebrow}</p><h4>{outcome.title}</h4><span>{outcome.detail}</span>
              {risks.length > 0 && <div className="risk-chips">{risks.map((risk) => <span key={risk} className="risk-chip"><AlertTriangle size={12} />{risk}</span>)}</div>}
            </div>
            <div className="tenant-opening-actions">
              {canActivate && <>
                <button className="button primary" onClick={() => setGate({ status: "active", tenantName: tenant.name })}><CheckCircle2 size={16} />激活租户</button>
                <button className="button secondary danger-text" onClick={() => setGate({ status: "archived", tenantName: tenant.name })}><Layers3 size={16} />关闭租户</button>
              </>}
              {active && <><button className="button secondary danger-text" onClick={() => setGate({ status: "suspended", tenantName: tenant.name })}><ShieldAlert size={16} />暂停客户使用</button>
                <button className="button secondary danger-text" onClick={() => setGate({ status: "archived", tenantName: tenant.name })}><Layers3 size={16} />关闭租户</button></>}
              {suspended && <><button className="button primary" disabled={!ready || activation.blocked} title={activation.blocked ? "安全验证尚未完成，暂不能恢复使用；需先解除安全限制后再操作" : !ready ? "安全检查未全部通过，暂不能恢复使用" : undefined} onClick={() => setGate({ status: "active", tenantName: tenant.name })}><CheckCircle2 size={16} />恢复客户使用</button>
                <button className="button secondary danger-text" onClick={() => setGate({ status: "archived", tenantName: tenant.name })}><Layers3 size={16} />关闭租户</button></>}
              {archived && <button className="button secondary danger-text" onClick={() => setGate({ status: "purged", tenantName: tenant.name })}><ShieldAlert size={16} />永久注销</button>}
            </div>
            {suspended && activation.blocked && <p className="tenant-blocked-hint">安全验证尚未完成，暂不能恢复使用；解除安全限制后本操作会自动恢复可用。</p>}
          </section>

          {activation.foundationOnly && !activation.blocked && <section className="tenant-360-activation tenant-360-span">
            <ShieldCheck size={16} />
            <div><strong>{activation.blocked ? "正式激活受安全条件保护" : "单租户安全保护阶段"}</strong><span>{activation.explanation}</span></div>
          </section>}

          <section className="tenant-basic-policy tenant-360-keyinfo tenant-360-span">
            <div className="tenant-opening-section-heading"><div><ClipboardList size={17} /><h4>概览</h4></div><span>{statusText(tenant.status)}</span></div>
            <div className="tenant-policy-rows">
              <div><span>当前套餐</span><strong>{subscription ? subscription.planName : businessLabel(planLabels, tenant.planKey, "版本待确认")}</strong><small>{subscription?.endsAt ? `当前账期至 ${formatDate(subscription.endsAt)}` : "尚未建立订阅账期"}</small></div>
              <div><span>负责人</span><strong>{tenant.ownerName ?? "未指派"}</strong><small>可在下方联系资料中维护</small></div>
              <div><span>开通进度</span><strong className="app-number">{readiness.percent}%</strong><small>安全检查通过 {readiness.passed} 项 / 共 {readiness.total} 项{readiness.failed > 0 ? `，未通过 ${readiness.failed} 项` : ""}</small></div>
              <div><span>下一步</span><strong>{activation.blocked ? "解除安全保护" : risks.length > 0 ? `${risks.length} 项需处理` : active ? "持续自动巡检" : "等待系统完成开通"}</strong><small>{activation.blocked ? "由平台技术团队完成跨租户隔离验证" : risks.length > 0 ? risks.join("；") : active ? "当前无需人工操作" : "异常时系统会转入人工队列"}</small></div>
            </div>
          </section>

          <section className="tenant-basic-policy tenant-360-contact tenant-360-span">
            <div className="tenant-opening-section-heading"><div><Users size={17} /><h4>联系人与账单资料</h4></div>
              {canUpdateProfile && !purged && <button className="button ghost" onClick={() => setShowProfileEdit(true)}>维护企业资料</button>}
            </div>
            <div className="tenant-policy-rows">
              <div><span>企业邮箱</span><strong>{tenant.contactEmail ?? "未登记"}</strong><small>接收企业账号与服务通知</small></div>
              <div><span>企业联系人</span><strong>{tenant.contactName ?? "未登记"}</strong><small>{tenant.contactPhone ?? "暂无联系电话"}</small></div>
              <div><span>账单档案</span><strong>{subscription?.billingCustomerRef ? "已建立" : "尚未建立"}</strong><small>建立订阅后自动生成</small></div>
            </div>
          </section>

          {subscription && <section className="tenant-basic-policy">
            <div className="tenant-opening-section-heading"><div><CreditCard size={17} /><h4>套餐与订阅</h4></div><span>{subscription ? statusText(subscription.status) : "尚未订阅"}</span></div>
            <div className="tenant-policy-rows">
              <div><span>当前版本</span><strong>{subscription ? `${subscription.planName}（第 ${subscription.version} 版，不可变）` : businessLabel(planLabels, tenant.planKey, "版本待确认")}</strong><small>套餐按版本固化，变更会生成新版本</small></div>
              <div><span>当前账期</span><strong>{subscription ? `${formatDate(subscription.startsAt)} 起${subscription.endsAt ? ` 至 ${formatDate(subscription.endsAt)}` : "，未设定到期"}` : "尚未建立"}</strong><small>{subscription?.endsAt ? `下次续费节点：${formatDate(subscription.endsAt)}` : "建立订阅后自动计算"}</small></div>
              <div><span>自动续费</span><strong>尚未接入</strong><small>自动续费策略尚未接入，续费请通过客户与商业的订阅管理操作</small></div>
              <div><span>待生效变更</span><strong>暂无</strong><small>当前没有排队中的套餐变更</small></div>
            </div>
          </section>}

          {view.features.length > 0 && <section className="tenant-basic-policy">
            <div className="tenant-opening-section-heading"><div><PackageCheck size={17} /><h4>可用功能</h4></div><span>{view.features.reduce((sum, group) => sum + group.items.filter((item) => item.state !== "unavailable").length, 0)} 项可用</span></div>
            <div className="tenant-feature-groups">
              {view.features.map((group) => <div key={group.domain} className="tenant-feature-group">
                <strong>{group.domainLabel}</strong>
                <div className="feature-chips">{group.items.map((item) => <span key={item.displayName} className={`feature-chip ${item.state}`}>{item.displayName}<i>{featureStateLabels[item.state]}</i></span>)}</div>
              </div>)}
            </div>
          </section>}

          {view.usage.length > 0 && <section className="tenant-basic-policy">
            <div className="tenant-opening-section-heading"><div><Gauge size={17} /><h4>用量与额度</h4></div><span>{view.usage.length} 项计量</span></div>
            <div className="tenant-usage-list">{view.usage.map((item) => {
                const percent = item.hardLimit > 0 ? Math.min(100, Math.round((item.used / item.hardLimit) * 100)) : 0;
                const softPercent = item.hardLimit > 0 && item.softLimit > 0 ? Math.min(100, Math.round((item.softLimit / item.hardLimit) * 100)) : null;
                return <div key={item.meterName} className="tenant-usage-meter">
                  <div className="tenant-usage-head"><strong>{item.meterName}</strong><span className="app-number">{item.used.toLocaleString("zh-CN")} / {item.hardLimit > 0 ? item.hardLimit.toLocaleString("zh-CN") : "未设上限"} {item.unit}（{item.period === "month" ? "按月" : "按账期"}）</span></div>
                  <div className={`progress usage-progress ${percent >= 100 ? "exhausted" : percent >= 80 ? "warning" : ""}`}><i style={{ width: `${percent}%` }} />{softPercent !== null && <b className="usage-soft-marker" style={{ left: `${softPercent}%` }} title="提醒线" />}</div>
                  <div className="tenant-usage-meta">
                    <span>提醒线 {item.softLimit > 0 ? `${item.softLimit.toLocaleString("zh-CN")} ${item.unit}` : "未设定"}</span>
                    <span>超额策略：达到上限后按平台保护策略限制该资源使用</span>
                    <span>预计耗尽日 {item.projectedExhaustionDate ?? "暂无法预计"}</span>
                    <span>更新时间 {formatDateTime(item.lastOccurredAt)}</span>
                  </div>
                </div>;
              })}</div>
          </section>}

          {view.connections.length > 0 && <section className="tenant-basic-policy">
            <div className="tenant-opening-section-heading"><div><Link2 size={17} /><h4>平台接入</h4></div><span>{view.connections.length} 个连接</span></div>
            <div className="tenant-360-rows">{view.connections.map((connection) => <div key={connection.id} className="tenant-360-row">
                <div><StatusBadge value={connection.status} /><strong>{connection.name}</strong></div>
                <small>最近成功 {formatDateTime(connection.lastSuccessAt)} · 最近检查 {formatDateTime(connection.lastCheckedAt)}{connection.lastError ? ` · 最近异常：${connection.lastError}` : ""}</small>
                <small>连接异常时请到「平台运行」发起健康检查与恢复，平台不保存也不展示任何密钥明文。</small>
              </div>)}</div>
          </section>}

          {view.serviceAccounts.length > 0 && <section className="tenant-basic-policy">
            <div className="tenant-opening-section-heading"><div><KeyRound size={17} /><h4>服务账号</h4></div><span>{view.serviceAccounts.length} 个账号</span></div>
            <p className="tenant-360-note">服务账号密钥仅在创建时显示一次，平台不保存明文；账号仅供系统对接使用。</p>
            <div className="tenant-360-rows">{view.serviceAccounts.map((account) => <div key={account.id} className="tenant-360-row">
                <div><StatusBadge value={account.status} /><strong>{account.name}</strong></div>
                <small>最近使用 {formatDateTime(account.lastUsedAt)} · 过期时间 {formatDateTime(account.expiresAt)}</small>
                <small>创建于 {formatDateTime(account.createdAt)}{account.revokedAt ? ` · 已于 ${formatDateTime(account.revokedAt)} 吊销` : ""}</small>
              </div>)}</div>
          </section>}

          {view.supportGrants.length > 0 && <section className="tenant-basic-policy">
            <div className="tenant-opening-section-heading"><div><LifeBuoy size={17} /><h4>支持记录</h4></div><span>{view.supportGrants.length} 条记录</span></div>
            <div className="tenant-360-rows">{view.supportGrants.map((grant) => <div key={grant.id} className="tenant-360-row">
                <div><StatusBadge value={grant.status} /><strong>{grant.reason}</strong></div>
                <small>处理人 {grant.operatorName ?? "—"} · 创建于 {formatDateTime(grant.createdAt)} · 到期 {formatDateTime(grant.expiresAt)}</small>
              </div>)}</div>
          </section>}

          {unavailableServiceLabels.length > 0 && <section className="tenant-basic-policy tenant-360-service-summary tenant-360-span">
            <div className="tenant-opening-section-heading"><div><PackageCheck size={17} /><h4>尚未开通的服务</h4></div><span>{unavailableServiceLabels.length} 项</span></div>
            <p>当前尚无 {unavailableServiceLabels.join("、")} 的可用记录。完成订阅和安全开通后，相关配置与运行状态会自动出现在这里。</p>
          </section>}

          <section className="tenant-basic-policy tenant-360-audit tenant-360-span">
            <div className="tenant-opening-section-heading"><div><ScrollText size={17} /><h4>审计时间线</h4></div><span>最近 {view.auditTrail.length} 条</span></div>
            {view.auditTrail.length === 0
              ? <p className="muted tenant-360-empty">暂无审计记录。</p>
              : <ol className="tenant-360-timeline">{view.auditTrail.map((event, index) => <li key={`${event.createdAt}-${index}`}>
                <strong>{event.actionLabel}</strong>
                <span>{auditActorLabel(event.actorEmail)} · {formatDateTime(event.createdAt)}</span>
              </li>)}</ol>}
          </section>

        </div>
      </div>;
    }}</RemotePageState>
  </Dialog>
  {gate && <ApprovalGateModal
    session={session}
    operation={{ action: "tenant.lifecycle", targetType: "tenant", targetId: tenantId, tenantId, payload: { targetStatus: gate.status } }}
    title={lifecycleCopy[gate.status].title}
    targetLabel={gate.tenantName}
    impact={lifecycleCopy[gate.status].impact}
    recovery={lifecycleCopy[gate.status].recovery}
    execute={(approvalId, reason) => executeTransition(gate.status, approvalId, reason)}
    onClose={() => setGate(null)}
    onExecuted={() => { setGate(null); void remote.reload(); onChanged(); }}
  />}
  {showProfileEdit && remote.state.status !== "loading" && remote.state.status !== "error" && <ProfileEditDialog
    view={remote.state.data}
    onClose={() => setShowProfileEdit(false)}
    onDone={() => { setShowProfileEdit(false); void remote.reload(); onChanged(); }}
  />}
  </>;
}

function ProfileEditDialog({ view, onClose, onDone }: { view: TenantUsageCenterView; onClose: () => void; onDone: () => void }) {
  const { tenant } = view;
  const [ownerAdminId, setOwnerAdminId] = useState(tenant.ownerAdminId ?? "");
  const [contactName, setContactName] = useState(tenant.contactName ?? "");
  const [contactEmail, setContactEmail] = useState(tenant.contactEmail ?? "");
  const [contactPhone, setContactPhone] = useState(tenant.contactPhone ?? "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [owners, setOwners] = useState<OwnerOption[]>([]);
  useEffect(() => {
    void api<{ owners: OwnerOption[] }>("/api/tenant-profile-options").then((data) => setOwners(data.owners)).catch(() => setOwners([]));
  }, []);
  const trimmed = (value: string) => value.trim();
  const valid = reason.trim().length >= 8
    && (!trimmed(contactEmail) || emailPattern.test(trimmed(contactEmail)));
  async function submit() {
    setBusy(true); setError("");
    try {
      await api(`/api/tenants/${tenant.id}/profile`, { method: "PATCH", body: JSON.stringify({
        reason: reason.trim(),
        ownerAdminId: ownerAdminId || null,
        contactName: trimmed(contactName) || null,
        contactEmail: trimmed(contactEmail) || null,
        contactPhone: trimmed(contactPhone) || null,
      })});
      onDone();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "保存失败，请稍后再试");
    } finally { setBusy(false); }
  }
  return <Dialog title="维护企业资料" description="维护企业负责人和日常联系方式；客户状态变更请使用对应操作。" onClose={onClose}>
    <div className="form-grid">
      <div className="full field-block"><span className="field-label-row">平台负责人</span>
        <V3Select value={ownerAdminId} onChange={setOwnerAdminId} ariaLabel="平台负责人"
          options={[{ value: "", label: "暂不指派" }, ...owners.map((item) => ({ value: item.id, label: item.displayName }))]} />
      </div>
      <label><span className="field-label-row">企业联系人</span><input value={contactName} onChange={(event) => setContactName(event.target.value)} /></label>
      <label><span className="field-label-row">联系邮箱</span><input value={contactEmail} onChange={(event) => setContactEmail(event.target.value)} inputMode="email" /></label>
      <label><span className="field-label-row">联系手机</span><input value={contactPhone} onChange={(event) => setContactPhone(event.target.value)} inputMode="numeric" /></label>
      <label className="full"><span className="field-label-row">变更原因（至少 8 个字，会写入审计）</span>
        <textarea rows={3} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="说明本次维护的事实依据，例如企业联系人变更" />
      </label>
      {error && <div className="full"><InlineFeedback tone="danger">{error}</InlineFeedback></div>}
      <div className="full dialog-footer">
        <button type="button" className="button secondary" onClick={onClose} disabled={busy}>取消</button>
        <button type="button" className="button primary" disabled={busy || !valid} onClick={() => void submit()}>{busy ? "正在保存…" : "保存"}</button>
      </div>
    </div>
  </Dialog>;
}
